import { useState, useEffect, useCallback } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography } from '@mui/material';

import HSRegionEditor from './components/HSRegionEditor';
import HSScoringEditor from './components/HSScoringEditor';
import {
    Debugger,
    QuestionMetaForm,
    TextEntryModal,
    QuestionTitleField,
    type TextEntryModalPartialProps,
} from '../_shared';
import { metaSchema } from '../../models/schema';
import { Constants } from '@/src/utils';
import { defaultMeta } from '../../models/questionDTOs';
import {
    ControlledDynamicTextEditor,
    isEditorContentEmpty,
    HotspotRenderer,
    type CDTEFieldType,
    type QtiHotspotQuestionDTO,
    type QuestionMetaDTO,
} from '@/src/lib';
import { renderLoaders } from '../../utils';
import { saveQuestion } from '../../services';
import { ErrorMessage, SimpleButton, SimpleSwitch, ControlledTextField } from '@/src/ui';

const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const config = {
    minRegions: 1,
    maxRegions: 20,
    minMaxSelections: 1,
    maxMaxSelections: 50,
};

const schema = yup.object({
    id: yup.number().optional(),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    prompt: yup
        .string()
        .test(
            'not-empty',
            'Prompt cannot be empty.',
            (value) => !isEditorContentEmpty(value || '')
        ),
    prompt_es: yup.string().when('$isSpanishAllowed', {
        is: true,
        then: (s) =>
            s
                .test(
                    'not-empty',
                    'Prompt (Spanish) cannot be empty.',
                    (value) => !isEditorContentEmpty(value || '')
                )
                .required('Prompt (Spanish) is required'),
        otherwise: (s) => s.optional(),
    }),
    backgroundImageUrl: yup.string().trim().required('Background image is required'),
    backgroundImageUrl_es: yup
        .string()
        .trim()
        .when('$isSpanishAllowed', {
            is: true,
            then: (s) => s.required('Background image (Spanish) is required'),
            otherwise: (s) => s.optional(),
        }),
    designCanvas: yup.object({
        width: yup.number().min(100).required('Width is required'),
        height: yup.number().min(100).required('Height is required'),
    }),
    designCanvas_es: yup.object({
        width: yup.number().required('Width is required'),
        height: yup.number().required('Height is required'),
    }),
    maxSelections: yup
        .number()
        .typeError('Must be a number')
        .integer('Must be an integer')
        .min(config.minMaxSelections, `Must be at least ${config.minMaxSelections}`)
        .max(config.maxMaxSelections, `Cannot exceed ${config.maxMaxSelections}`)
        .required('Max selections is required'),
    regions: yup
        .array()
        .of(
            yup.object({
                id: yup.string().trim().required('ID is required'),
                label: yup.string().trim().required('Label is required'),
                geometry: yup
                    .object({
                        type: yup.string().oneOf(['rect', 'circle']).required('Type is required'),
                        x: yup.number().when('type', {
                            is: 'rect',
                            then: (s) => s.required('X is required').min(0).max(1),
                            otherwise: (s) => s.optional(),
                        }),
                        y: yup.number().when('type', {
                            is: 'rect',
                            then: (s) => s.required('Y is required').min(0).max(1),
                            otherwise: (s) => s.optional(),
                        }),
                        width: yup.number().when('type', {
                            is: 'rect',
                            then: (s) => s.required('Width is required').min(0.01).max(1),
                            otherwise: (s) => s.optional(),
                        }),
                        height: yup.number().when('type', {
                            is: 'rect',
                            then: (s) => s.required('Height is required').min(0.01).max(1),
                            otherwise: (s) => s.optional(),
                        }),
                        cx: yup.number().when('type', {
                            is: 'circle',
                            then: (s) => s.required('Center X is required').min(0).max(1),
                            otherwise: (s) => s.optional(),
                        }),
                        cy: yup.number().when('type', {
                            is: 'circle',
                            then: (s) => s.required('Center Y is required').min(0).max(1),
                            otherwise: (s) => s.optional(),
                        }),
                        r: yup.number().when('type', {
                            is: 'circle',
                            then: (s) => s.required('Radius is required').min(0.01).max(0.5),
                            otherwise: (s) => s.optional(),
                        }),
                    })
                    .required('Geometry is required'),
                visible: yup.boolean().required(),
                weight: yup.number().typeError('Must be a number').required('Weight is required'),
            })
        )
        .min(config.minRegions, `At least ${config.minRegions} region is required`)
        .max(config.maxRegions, `Maximum ${config.maxRegions} regions allowed`),
    targetWeight: yup
        .number()
        .typeError('Must be a number')
        .min(0, 'Must be ≥ 0')
        .required('Target weight is required'),
    matchMode: yup.mixed().oneOf(['exact', 'atLeast', 'atMost']).required('Match mode is required'),
    meta: metaSchema.required(),
});

const defaultCanvas = { width: 1000, height: 600 };

interface HotspotEditorProps {
    initialModel?: QtiHotspotQuestionDTO;
}

export default function HotspotEditor({ initialModel }: HotspotEditorProps) {
    const isEdit = !!initialModel;
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);
    const [modalProps, setModalProps] = useState<TextEntryModalPartialProps | null>(null);

    const [currentLanguage, setCurrentLanguage] = useState<'en' | 'es'>('en');

    const defaultValues: QtiHotspotQuestionDTO = initialModel
        ? initialModel
        : {
              title: '',
              prompt: '',
              prompt_es: '',
              backgroundImageUrl: '',
              backgroundImageUrl_es: '',
              designCanvas: { ...defaultCanvas },
              designCanvas_es: { ...defaultCanvas },
              maxSelections: 1,
              regions: [],
              targetWeight: 1,
              matchMode: 'exact',
              meta: { ...defaultMeta, questionTypeMasterId: qtm.HOTSPOT },
          };

    const [formContext, setFormContext] = useState({
        isSpanishAllowed: defaultValues.meta.isSpanishAllowed,
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiHotspotQuestionDTO>({
        defaultValues,
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
        context: formContext,
    });

    const question = watch();

    useEffect(() => {
        setFormContext({ isSpanishAllowed: question.meta.isSpanishAllowed });
    }, [question.meta.isSpanishAllowed]);

    const handleLanguageSet = useCallback(
        (language: 'en' | 'es') => {
            setCurrentLanguage(language);
            if (language === 'en') return;

            if (!question.prompt_es) {
                setValue('prompt_es', question.prompt);
            }
            if (!question.backgroundImageUrl_es) {
                setValue('backgroundImageUrl_es', question.backgroundImageUrl);
                setValue('designCanvas_es', question.designCanvas);
            }
        },
        [question, setValue]
    );

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);
        try {
            const viewMode = isEdit ? 'edit' : 'create';
            await saveQuestion(question, viewMode);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', { type: 'manual', message: e.message || 'Unknown error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <form
                key={`HotspotEditor-${question.meta.isSpanishAllowed}`}
                className="p-4 space-y-4"
                onSubmit={handleSubmit(onSubmit)}
            >
                {modalProps && (
                    <TextEntryModal
                        initialData={modalProps.initialData}
                        fieldName={modalProps.fieldName}
                        friendlyName={modalProps.friendlyName}
                        textMode={modalProps.textMode}
                        error={modalProps.error}
                        control={control}
                        handleClose={() => setModalProps(null)}
                    />
                )}
                {renderLoaders(loading, success, error, isEdit)}

                <h2>Hotspot Question Title:</h2>
                <QuestionTitleField
                    id={question.id}
                    title={question.title}
                    control={control}
                    setIsTitleError={setIsTitleError}
                    error={errors.title}
                />

                <div className="my-4" />

                <div className="flex flex-row gap-4">
                    <h2>Prompt:</h2>
                    {question.meta.isSpanishAllowed && (
                        <div
                            onClick={() =>
                                setModalProps({
                                    initialData: question.prompt_es,
                                    fieldName: 'prompt_es',
                                    friendlyName: 'Prompt (Spanish)',
                                    textMode: 'rich',
                                    error: errors.prompt_es?.message,
                                })
                            }
                            style={{ cursor: 'pointer' }}
                        >
                            Edit Spanish
                        </div>
                    )}
                    <ErrorMessage error={errors.prompt_es} />
                </div>
                <ControlledDynamicTextEditor
                    name="prompt"
                    control={control}
                    initialValue={question.prompt}
                    setOutput={(val: string, field: CDTEFieldType) => {
                        field.onChange(val);
                        setValue('prompt', val, { shouldValidate: true });
                    }}
                    style={{ minHeight: '150px' }}
                />
                <ErrorMessage error={errors.prompt} />

                <div className="my-4" />

                {/* Max selections control */}
                <h2>Selection Settings:</h2>
                <div className="flex items-center gap-4">
                    <ControlledTextField
                        name="maxSelections"
                        control={control}
                        label="Maximum allowed selections"
                        type="number"
                        error={errors.maxSelections}
                        inputProps={{ min: 1, max: config.maxMaxSelections }}
                        sx={{ width: 200 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 300 }}>
                        This is the number of regions the student can select.
                    </Typography>
                </div>

                <div className="my-4" />

                {question.meta.isSpanishAllowed && (
                    <div>
                        <span>Region editor language: </span>
                        <SimpleSwitch
                            name="hs-currentLanguage"
                            checked={currentLanguage === 'es'}
                            onChange={(e) => setCurrentLanguage(e.target.checked ? 'es' : 'en')}
                            label={currentLanguage === 'en' ? 'English' : 'Spanish'}
                        />
                    </div>
                )}

                <h2>Hotspot Regions:</h2>
                <HSRegionEditor
                    question={question}
                    currentLanguage={currentLanguage}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                />

                <div className="my-4" />

                {question.meta.isHandScored ? (
                    <h2>Scoring disabled for hand score questions</h2>
                ) : (
                    <>
                        <h2>Scoring:</h2>
                        <HSScoringEditor
                            control={control}
                            errors={errors}
                            regions={question.regions}
                            targetWeight={question.targetWeight}
                            matchMode={question.matchMode}
                            maxSelections={question.maxSelections}
                            setValue={setValue}
                        />
                    </>
                )}

                <QuestionMetaForm
                    rhfProps={{
                        control,
                        errors: errors.meta,
                        watch,
                    }}
                    handleLanguageSet={handleLanguageSet}
                    disableList={['shouldShuffleOptions', 'shouldShowKeypad']}
                    metaValues={watch('meta')}
                    setMetaValues={(meta: QuestionMetaDTO) => setValue('meta', meta)}
                />

                <div className="border rounded p-4 bg-white max-w-4xl space-y-4 mt-4">
                    <h4 className="font-semibold mb-1">Interactive Preview</h4>
                    <HotspotRenderer
                        question={question}
                        options={{
                            showLanguageToggle: question.meta.isSpanishAllowed,
                            showScoring: true,
                        }}
                    />
                </div>

                <div className="flex items-center space-x-2">
                    <div>
                        <SimpleButton
                            type="submit"
                            color="primary"
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                            disabled={isSubmitting || loading}
                        >
                            {isEdit ? 'Update Question' : 'Create Question'}
                        </SimpleButton>
                    </div>
                </div>
                <ErrorMessage
                    error={
                        Object.keys(errors).length ? 'Please correct the errors to continue' : ''
                    }
                />
                {renderLoaders(loading, success, error, isEdit)}

                <Debugger question={question} errors={errors} />
            </form>
        </>
    );
}
